import { ChevronDown, Info } from 'lucide-react'
import { useState } from 'react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { CASINO_VOLATILITY_LABELS } from '@/config/casino'
import { cn } from '@/lib/utils'
import { DetailGrid } from '@/components/play/DetailGrid'
import type { DetailRow } from '@/components/play/DetailRowItem'
import type { CasinoGame } from '@/types/casino'

interface CasinoPlayDetailsProps {
	game: CasinoGame
}

export function CasinoPlayDetails({ game }: CasinoPlayDetailsProps) {
	const [isOpen, setIsOpen] = useState(true)
	const rtp = game.parameters?.rtp ?? game.rtp
	const volatility = game.parameters?.volatility

	const rows: DetailRow[] = [
		{ label: 'Technology', value: game.technology || '-' },
		{ label: 'Mobile Support', value: game.is_mobile == null ? '-' : game.is_mobile ? 'Yes' : 'No' },
		{ label: 'Free Spins Support', value: game.has_freespins == null ? '-' : game.has_freespins ? 'Yes' : 'No' },
		{ label: 'Table Support', value: game.has_tables == null ? '-' : game.has_tables ? 'Yes' : 'No' },
		{ label: 'RTP', value: rtp == null ? '-' : `${rtp}%` },
		{
			label: 'Volatility',
			value: volatility == null ? '-' : (CASINO_VOLATILITY_LABELS[volatility] ?? String(volatility)),
		},
		{ label: 'Lines Count', value: game.parameters?.lines_count == null ? '-' : String(game.parameters.lines_count) },
		{ label: 'Reels Count', value: game.parameters?.reels_count == null ? '-' : String(game.parameters.reels_count) },
	]

	return (
		<Collapsible open={isOpen} onOpenChange={setIsOpen} className='overflow-hidden rounded-2xl border border-border bg-card'>
			<CollapsibleTrigger className='flex w-full items-start justify-between gap-4 px-4 py-4 text-left transition-colors hover:bg-accent/40 sm:px-6 sm:py-5'>
				<div className='flex min-w-0 flex-col gap-2'>
					<div className='flex flex-wrap items-center gap-2'>
						<h1 className='text-xl font-semibold tracking-[-0.12px] text-foreground sm:text-2xl'>{game.casino_name || '-'}</h1>
						{rtp != null ? (
							<span className='inline-flex items-center gap-1 rounded-md border border-primary-border bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary'>
								{`${rtp}% RTP`}
								<Info className='h-3.5 w-3.5 text-muted-foreground' aria-hidden />
							</span>
						) : null}
					</div>
					<p className='text-sm text-muted-foreground'>By {game.provider?.provider_name || '-'}</p>
				</div>
				<ChevronDown className={cn('mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-180')} aria-hidden />
			</CollapsibleTrigger>

			<CollapsibleContent>
				<DetailGrid rows={rows} />
			</CollapsibleContent>
		</Collapsible>
	)
}
